import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { PageHeader, useToast } from 'components/admin';
import { Button, Checkbox, Input } from 'components/common';
import { createBusinessLicense, getAdminBusinessLicenseById, updateBusinessLicense } from 'apis/adminBusinessLicenses';
import {
    useActiveLanguagesQuery,
    useAdminCreateMutation,
    useAdminDetailQuery,
    useAdminUpdateMutation,
} from 'hooks';
import '../../../styles/admin-shared.scss';

const BusinessLicensesForm = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const toast = useToast();
    const [errors, setErrors] = useState({});
    const [activeTab, setActiveTab] = useState(0);
    const [formData, setFormData] = useState({ licenseNumber: '', isActive: true, translation: [] });

    const { data: languages = [] } = useActiveLanguagesQuery();
    const { data: detail, error: detailError } = useAdminDetailQuery('business-licenses', id, getAdminBusinessLicenseById);
    const createMutation = useAdminCreateMutation('business-licenses', createBusinessLicense);
    const updateMutation = useAdminUpdateMutation('business-licenses', updateBusinessLicense);
    const loading = createMutation.isPending || updateMutation.isPending;

    useEffect(() => {
        if (!id && languages.length) {
            setFormData(prev => ({ ...prev, translation: languages.map(l => ({ languageId: l.id, name: '', issuer: '' })) }));
        }
    }, [id, languages]);

    useEffect(() => {
        if (detail) setFormData(detail);
    }, [detail]);

    useEffect(() => {
        if (detailError) toast.error(detailError.message || 'Tải dữ liệu thất bại');
    }, [detailError, toast]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    };
    const handleTranslationChange = (langId, field, value) => {
        setFormData(prev => ({ ...prev, translation: prev.translation.map(t => t.languageId === langId ? { ...t, [field]: value } : t) }));
    };
    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const res = id
                ? await updateMutation.mutateAsync({ id, data: formData })
                : await createMutation.mutateAsync(formData);
            if (res.success) { toast.success(id ? 'Cập nhật thành công' : 'Thêm mới thành công'); navigate('/admin/business-licenses'); }
            else { if (res.messageError) setErrors(res.messageError); else toast.error(res.message || 'Lưu thất bại'); }
        } catch { toast.error('Lưu thất bại'); }
    };

    return (
        <div className="admin-page">
            <PageHeader title={id ? 'Cập nhật Giấy phép Kinh doanh' : 'Thêm Giấy phép Kinh doanh'} subtitle="Nhập thông tin giấy phép kinh doanh chính thức" />
            <form className="form-card" onSubmit={handleSubmit}>
                <p className="form-section-title">Chi tiết Giấy phép</p>
                <Input label="Số giấy phép *" name="licenseNumber" value={formData.licenseNumber} onChange={handleChange} required error={errors.licenseNumber} placeholder="VD: 0123456789" />
                <Checkbox label="Hoạt động" name="isActive" checked={formData.isActive} onChange={handleChange} />
                {languages.length > 0 && (
                    <>
                        <p className="form-section-title">Bản dịch</p>
                        <div className="translation-tabs">
                            <div className="tab-header">{languages.map((lang, i) => <button key={lang.id} type="button" className={`tab-btn ${activeTab === i ? 'active' : ''}`} onClick={() => setActiveTab(i)}>{lang.name}</button>)}</div>
                            {languages.map((lang, i) => {
                                const t = formData.translation.find(t => t.languageId === lang.id) || {};
                                return activeTab === i ? (
                                    <div key={lang.id} className="tab-content">
                                        <Input label="Tên Giấy phép" value={t.name || ''} onChange={e => handleTranslationChange(lang.id, 'name', e.target.value)} />
                                        <Input label="Nơi cấp" value={t.issuer || ''} onChange={e => handleTranslationChange(lang.id, 'issuer', e.target.value)} placeholder="Cơ quan cấp..." />
                                    </div>
                                ) : null;
                            })}
                        </div>
                    </>
                )}
                <div className="form-actions">
                    <Button type="submit" disabled={loading}>{loading ? 'Đang lưu...' : id ? 'Cập nhật Giấy phép' : 'Thêm Giấy phép'}</Button>
                    <Button type="button" variant="secondary" onClick={() => navigate('/admin/business-licenses')}>Hủy</Button>
                </div>
            </form>
        </div>
    );
};

export default BusinessLicensesForm;
